<script setup>
import { computed } from 'vue';
import { useMessageContext } from '../../provider.js';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { MESSAGE_VARIANTS } from '../../constants';

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
});

const { variant, contentAttributes } = useMessageContext();

// Ensure readable text on dark agent/bot bubbles in light mode
// and force black text for deleted message notice in light mode
const proseColorClasses = computed(() => {
  const isDeleted =
    contentAttributes.value?.deleted ||
    (typeof props.content === 'string' &&
      props.content.trim().startsWith('⛔'));
  const isAgentOrBot =
    variant.value === MESSAGE_VARIANTS.AGENT ||
    variant.value === MESSAGE_VARIANTS.BOT;
  const isTemplate = variant.value === MESSAGE_VARIANTS.TEMPLATE;
  if (isDeleted) {
    return [
      'text-black',
      'prose-headings:text-black',
      'prose-strong:text-black',
      'prose-a:text-black',
      'prose-code:text-black',
      'prose-th:text-black',
      'prose-code:bg-n-alpha-3',
      'dark:text-n-slate-12',
      'dark:prose-headings:text-n-slate-12',
      'dark:prose-strong:text-n-slate-12',
      'dark:prose-a:text-n-slate-12',
      'dark:prose-code:text-n-slate-12',
      'dark:prose-code:bg-n-alpha-3',
      'dark:prose-th:text-n-slate-12',
    ];
  }
  if (isAgentOrBot || isTemplate) {
    return [
      'text-white',
      'prose-headings:text-white',
      'prose-strong:text-white',
      'prose-a:text-white',
      'prose-code:text-white',
      'prose-code:bg-white/10',
      'prose-pre:text-white',
      'prose-pre:bg-white/10',
      'prose-th:text-white',
      'dark:prose-headings:text-white',
      'dark:prose-strong:text-white',
      'dark:prose-a:text-white',
      'dark:prose-code:text-white',
      'dark:prose-code:bg-white/10',
      'dark:prose-pre:text-white',
      'dark:prose-pre:bg-white/10',
      'dark:prose-th:text-white',
    ];
  }
  return [];
});

const formattedContent = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) {
    return props.content;
  }

  return new MessageFormatter(props.content).formattedMessage;
});
</script>

<template>
  <span
    v-dompurify-html="formattedContent"
    class="prose prose-bubble"
    :class="[proseColorClasses]"
  />
</template>
